import { useState, useEffect } from 'react';
import { userService, type User, type CreateUserRequest, type UpdateUserRequest } from '../services/userService';
import { clientService, type Client } from '../services/clientService';
import { projectService, type Project } from '../services/projectService';
import { useCanManageUsers } from '../hooks/usePermissions';
import PageHeader from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';

const ROLES = ['SysAdmin', 'DevOps', 'ClientAdmin', 'ProjectMgr', 'Engineer', 'Viewer'];

const FormField = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="grid grid-cols-3 items-center gap-3">
    <label className="text-sm text-slate-500 text-right">{label}</label>
    <div className="col-span-2">{children}</div>
  </div>
);

const fieldCls = "w-full text-sm border border-slate-200 rounded px-2 py-1.5 focus:outline-none focus:border-primary-400";

interface UsersByClientAndProject {
  [clientId: string]: {
    clientName: string;
    projects: {
      [projectId: string]: {
        projectName: string;
        users: User[];
      };
    };
    unassignedUsers: User[];
  };
}

export default function SettingsUsers() {
  const canManageUsers = useCanManageUsers();
  const [users, setUsers] = useState<User[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [groupedUsers, setGroupedUsers] = useState<UsersByClientAndProject>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [createFormData, setCreateFormData] = useState<CreateUserRequest>({
    first_name: '',
    family_name: '',
    email: '',
    role: 'Engineer',
    client_id: '',
    username: '',
  });
  const [editFormData, setEditFormData] = useState({
    first_name: '',
    family_name: '',
    email: '',
    role: '',
    client_id: '',
    username: '',
  });

  useEffect(() => {
    loadUsers();
    loadClients();
    loadProjects();
  }, []);

  useEffect(() => {
    groupUsersByClientAndProject();
  }, [users, clients, projects, searchTerm]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await userService.getAll();
      setUsers(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadClients = async () => {
    try {
      const data = await clientService.getAll();
      console.log('Loaded clients:', data);
      setClients(data);
    } catch (err) {
      console.error('Failed to load clients:', err);
      const errorMsg = err instanceof Error ? err.message : 'Failed to load clients';
      setError(`Cannot load clients: ${errorMsg}. Unable to create team members without client list.`);
    }
  };

  const loadProjects = async () => {
    try {
      const data = await projectService.getProjects();
      setProjects(data);
    } catch (err) {
      console.error('Failed to load projects:', err);
    }
  };

  const groupUsersByClientAndProject = () => {
    const grouped: UsersByClientAndProject = {};

    // Filter users by client search term
    let filteredUsers = users;
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      const matchingClients = clients.filter(c => 
        c.company_name.toLowerCase().includes(term)
      );
      const matchingClientIds = new Set(matchingClients.map(c => c.id));
      filteredUsers = users.filter(u => u.client_id && matchingClientIds.has(u.client_id));
    }

    // Group users by client
    filteredUsers.forEach(user => {
      const clientId = user.client_id || 'no-client';
      const client = clients.find(c => c.id === clientId);
      const clientName = client ? client.company_name : 'No Client';

      if (!grouped[clientId]) {
        grouped[clientId] = {
          clientName,
          projects: {},
          unassignedUsers: []
        };
      }

      // Check if user is an Admin role
      const adminRoles = ['SysAdmin', 'DevOps', 'ClientAdmin'];
      if (adminRoles.includes(user.role || '')) {
        // Add to Admin category
        const adminProjectId = 'admin-category';
        if (!grouped[clientId].projects[adminProjectId]) {
          grouped[clientId].projects[adminProjectId] = {
            projectName: 'Admin',
            users: []
          };
        }
        grouped[clientId].projects[adminProjectId].users.push(user);
      } else {
        // User is ProjectMgr, Engineer, or Viewer - add to their assigned projects
        const assignedProjects = user.assigned_projects || [];
        if (assignedProjects.length === 0) {
          // No assigned project - still add them somewhere (shouldn't happen for project roles)
          grouped[clientId].unassignedUsers.push(user);
        } else {
          // Add user to each project they're assigned to
          assignedProjects.forEach((projectId: string) => {
            const project = projects.find(p => p.id === projectId);
            const projectName = project ? project.name : 'Unknown Project';

            if (!grouped[clientId].projects[projectId]) {
              grouped[clientId].projects[projectId] = {
                projectName,
                users: []
              };
            }

            grouped[clientId].projects[projectId].users.push(user);
          });
        }
      }
    });

    setGroupedUsers(grouped);
  };

  // Unused helper functions - keeping for future use
  // const applySearch = () => {
  //   groupUsersByClientAndProject();
  // };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await userService.create(createFormData);
      setShowCreateModal(false);
      setCreateFormData({
        first_name: '',
        family_name: '',
        email: '',
        role: 'Engineer',
        client_id: '',
        username: '',
      });
      await loadUsers();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to create team member');
    }
  };

  const handleEditUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;
    try {
      const updateData: UpdateUserRequest = {
        first_name: editFormData.first_name,
        family_name: editFormData.family_name,
        email: editFormData.email || undefined,
        role: editFormData.role,
        client_id: editFormData.client_id || undefined,
        username: editFormData.username || undefined,
      };
      await userService.update(editingUser.id, updateData);
      setShowEditModal(false);
      setEditingUser(null);
      await loadUsers();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update team member');
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!confirm('Are you sure you want to delete this team member?')) return;
    try {
      await userService.delete(userId);
      await loadUsers();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete team member');
    }
  };

  const openEditModal = (user: User) => {
    if (!user || !user.id) {
      console.error('Invalid user object:', user);
      return;
    }

    setEditingUser(user);
    setEditFormData({
      first_name: user.first_name || '',
      family_name: user.family_name || '',
      email: user.email || '',
      role: user.role || 'Engineer',
      client_id: user.client_id || '',
      username: user.username || '',
    });
    setShowEditModal(true);
  };

  const isUserActive = (lastConnected: string | null, thresholdDays: number = 90): boolean => {
    if (!lastConnected) return false;
    const lastConnectedDate = new Date(lastConnected);
    const daysSinceConnection = (Date.now() - lastConnectedDate.getTime()) / (1000 * 60 * 60 * 24);
    return daysSinceConnection < thresholdDays;
  };

  const formatLastConnected = (lastConnected: string | null): string => {
    if (!lastConnected) return 'Never';
    return new Date(lastConnected).toLocaleDateString();
  };

  const hasAnyUsers = Object.keys(groupedUsers).length > 0;

  if (loading) {
    return <div className="px-6 py-4 text-sm text-slate-400">Loading team members...</div>;
  }

  if (error) {
    return <div className="px-6 py-4 text-sm text-red-600">{error}</div>;
  }

  return (
    <div className="max-w-5xl mx-auto">
      <PageHeader
        title="Team Management"
        actions={
          <div className="flex items-center gap-2">
            <input
              type="text" placeholder="Search by client..."
              className="text-sm border border-slate-200 rounded px-2 py-1 focus:outline-none focus:border-primary-400 w-44"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {canManageUsers && (
              <Button size="sm" onClick={() => setShowCreateModal(true)}>Create</Button>
            )}
          </div>
        }
      />

      <div className="px-6 py-4">
        {!hasAnyUsers ? (
          <p className="text-sm text-slate-400">No team members found.</p>
        ) : (
          <div className="space-y-4">
            {Object.entries(groupedUsers).map(([clientId, clientData]) => (
              <div key={clientId}>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">{clientData.clientName}</p>
                <div className="space-y-2">
                  {Object.entries(clientData.projects)
                    .sort(([idA], [idB]) => {
                      if (idA === 'admin-category') return -1;
                      if (idB === 'admin-category') return 1;
                      return clientData.projects[idA].projectName.localeCompare(clientData.projects[idB].projectName);
                    })
                    .map(([groupId, groupData]) => (
                      <div key={groupId} className="border border-slate-100 rounded">
                        <p className="px-3 py-1.5 text-xs font-medium text-slate-400 bg-slate-50 rounded-t border-b border-slate-100">
                          {groupId === 'admin-category' ? 'Admin' : groupData.projectName}
                        </p>
                        <div>
                          {groupData.users.map(u => (
                            <div key={u.id} className="flex items-center gap-3 px-3 py-1.5 border-b border-slate-50 last:border-0 hover:bg-slate-50/50">
                              <span className="text-sm text-slate-700 w-40 flex-shrink-0">{u.first_name} {u.family_name}</span>
                              <span className="text-xs text-slate-500 w-24 flex-shrink-0">{u.role}</span>
                              <span className={`text-xs w-24 flex-shrink-0 ${isUserActive(u.last_connected) ? 'text-green-600' : 'text-slate-400'}`}>
                                {formatLastConnected(u.last_connected)}
                              </span>
                              {canManageUsers && (
                                <div className="flex gap-1 ml-auto">
                                  <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => openEditModal(u)}>Edit</Button>
                                  <Button size="sm" variant="ghost" className="h-6 px-2 text-xs text-red-500 hover:text-red-700" onClick={() => handleDeleteUser(u.id)}>Delete</Button>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  {clientData.unassignedUsers.length > 0 && (
                    <div className="border border-slate-100 rounded">
                      <p className="px-3 py-1.5 text-xs font-medium text-slate-400 bg-slate-50 rounded-t border-b border-slate-100">Unassigned</p>
                      {clientData.unassignedUsers.map(u => (
                        <div key={u.id} className="flex items-center gap-3 px-3 py-1.5 border-b border-slate-50 last:border-0 hover:bg-slate-50/50">
                          <span className="text-sm text-slate-700 w-40 flex-shrink-0">{u.first_name} {u.family_name}</span>
                          <span className="text-xs text-slate-500 w-24 flex-shrink-0">{u.role}</span>
                          <span className={`text-xs w-24 flex-shrink-0 ${isUserActive(u.last_connected) ? 'text-green-600' : 'text-slate-400'}`}>
                            {formatLastConnected(u.last_connected)}
                          </span>
                          {canManageUsers && (
                            <div className="flex gap-1 ml-auto">
                              <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => openEditModal(u)}>Edit</Button>
                              <Button size="sm" variant="ghost" className="h-6 px-2 text-xs text-red-500 hover:text-red-700" onClick={() => handleDeleteUser(u.id)}>Delete</Button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Create Team Member</DialogTitle></DialogHeader>
          <form id="create-user-form" onSubmit={handleCreateUser} className="space-y-3 py-2">
            <FormField label="First Name *">
              <input className={fieldCls} type="text" required maxLength={100} value={createFormData.first_name} onChange={(e) => setCreateFormData({ ...createFormData, first_name: e.target.value })} />
            </FormField>
            <FormField label="Family Name *">
              <input className={fieldCls} type="text" required maxLength={100} value={createFormData.family_name} onChange={(e) => setCreateFormData({ ...createFormData, family_name: e.target.value })} />
            </FormField>
            <FormField label="Email">
              <input className={fieldCls} type="email" maxLength={200} value={createFormData.email} onChange={(e) => setCreateFormData({ ...createFormData, email: e.target.value })} />
            </FormField>
            <FormField label="Client *">
              <select className={fieldCls} required value={createFormData.client_id} onChange={(e) => setCreateFormData({ ...createFormData, client_id: e.target.value })}>
                <option value="">Select a client...</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
              </select>
            </FormField>
            <FormField label="Role *">
              <select className={fieldCls} required value={createFormData.role} onChange={(e) => setCreateFormData({ ...createFormData, role: e.target.value })}>
                {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </FormField>
            <FormField label="Username">
              <input className={fieldCls} type="text" minLength={3} maxLength={50} value={createFormData.username} onChange={(e) => setCreateFormData({ ...createFormData, username: e.target.value })} />
            </FormField>
          </form>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button type="submit" form="create-user-form">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Edit Team Member</DialogTitle></DialogHeader>
          <form id="edit-user-form" onSubmit={handleEditUser} className="space-y-3 py-2">
            <FormField label="First Name *">
              <input className={fieldCls} type="text" required maxLength={100} value={editFormData.first_name} onChange={(e) => setEditFormData({ ...editFormData, first_name: e.target.value })} />
            </FormField>
            <FormField label="Family Name *">
              <input className={fieldCls} type="text" required maxLength={100} value={editFormData.family_name} onChange={(e) => setEditFormData({ ...editFormData, family_name: e.target.value })} />
            </FormField>
            <FormField label="Email">
              <input className={fieldCls} type="email" maxLength={200} value={editFormData.email} onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })} />
            </FormField>
            <FormField label="Client *">
              <select className={fieldCls} required value={editFormData.client_id} onChange={(e) => setEditFormData({ ...editFormData, client_id: e.target.value })}>
                <option value="">Select a client...</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
              </select>
            </FormField>
            <FormField label="Role *">
              <select className={fieldCls} required value={editFormData.role} onChange={(e) => setEditFormData({ ...editFormData, role: e.target.value })}>
                {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </FormField>
            <FormField label="Username">
              <input className={fieldCls} type="text" minLength={3} maxLength={50} value={editFormData.username} onChange={(e) => setEditFormData({ ...editFormData, username: e.target.value })} />
            </FormField>
            <FormField label="Last Connected">
              <span className={`text-sm ${isUserActive(editingUser?.last_connected || null) ? 'text-green-600' : 'text-slate-400'}`}>
                {formatLastConnected(editingUser?.last_connected || null)}
              </span>
            </FormField>
          </form>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowEditModal(false)}>Cancel</Button>
            <Button type="submit" form="edit-user-form">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

